import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, CheckIcon, SparklesIcon } from 'lucide-react';
const steps = [{
  id: 1,
  title: 'Business Info',
  description: 'Tell us about your business'
}, {
  id: 2,
  title: 'Brand Voice',
  description: 'Define your brand personality'
}, {
  id: 3,
  title: 'Brand Colors',
  description: 'Choose your color palette'
}, {
  id: 4,
  title: 'Connect Accounts',
  description: 'Link your social media'
}];
export function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const handleComplete = () => {
    navigate('/dashboard');
  };
  return <div className="min-h-screen w-full bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 dark:from-gray-950 dark:via-purple-950/20 dark:to-blue-950/20 flex items-center justify-center p-6" data-id="element-156">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="w-full max-w-4xl" data-id="element-157">
        <div className="text-center mb-12" data-id="element-158">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-159">
            <SparklesIcon className="w-8 h-8 text-purple-600" data-id="element-160" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent" data-id="element-161">
              Welcome to GeoPulse AI
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-162">
            Let's set up your hyperlocal marketing assistant
          </p>
        </div>
        <div className="flex justify-center mb-12" data-id="element-163">
          {steps.map((step, idx) => <div key={step.id} className="flex items-center" data-id="element-164">
              <div className="flex flex-col items-center" data-id="element-165">
                <motion.div whileHover={{
              scale: 1.1
            }} className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${currentStep > step.id ? 'bg-purple-600 text-white' : currentStep === step.id ? 'bg-purple-600 text-white ring-4 ring-purple-200 dark:ring-purple-900' : 'bg-gray-200 dark:bg-gray-800 text-gray-500'}`} data-id="element-166">
                  {currentStep > step.id ? <CheckIcon className="w-6 h-6" data-id="element-167" /> : step.id}
                </motion.div>
                <p className="text-xs mt-2 font-medium text-gray-600 dark:text-gray-400" data-id="element-168">
                  {step.title}
                </p>
              </div>
              {idx < steps.length - 1 && <div className={`w-24 h-1 mx-4 rounded ${currentStep > step.id ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-800'}`} data-id="element-169" />}
            </div>)}
        </div>
        <motion.div key={currentStep} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-800" data-id="element-170">
          <AnimatePresence mode="wait" data-id="element-171">
            {currentStep === 1 && <Step1 data-id="element-172" />}
            {currentStep === 2 && <Step2 data-id="element-173" />}
            {currentStep === 3 && <Step3 data-id="element-174" />}
            {currentStep === 4 && <Step4 data-id="element-175" />}
          </AnimatePresence>
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-800" data-id="element-176">
            <button onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="px-6 py-2 rounded-xl font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-177">
              Back
            </button>
            <button onClick={() => {
            if (currentStep === 4) handleComplete();else setCurrentStep(Math.min(4, currentStep + 1));
          }} className="px-6 py-2 rounded-xl font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center gap-2" data-id="element-178">
              {currentStep === 4 ? 'Complete Setup' : 'Continue'}
              <ChevronRightIcon className="w-5 h-5" data-id="element-179" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}
function Step1() {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="space-y-6" data-id="element-180">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-181">
        Tell us about your business
      </h2>
      <div className="space-y-4" data-id="element-182">
        <div data-id="element-183">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-184">
            Business Name
          </label>
          <input type="text" placeholder="e.g., Sunset Coffee Roasters" className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none" data-id="element-185" />
        </div>
        <div data-id="element-186">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-187">
            Industry
          </label>
          <select className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none" data-id="element-188">
            <option data-id="element-189">Coffee Shop</option>
            <option data-id="element-190">Restaurant</option>
            <option data-id="element-191">Retail Store</option>
            <option data-id="element-192">Fitness Studio</option>
            <option data-id="element-193">Salon & Spa</option>
            <option data-id="element-194">Other</option>
          </select>
        </div>
        <div data-id="element-195">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-196">
            Location / ZIP Code
          </label>
          <input type="text" placeholder="e.g., 94103" className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none" data-id="element-197" />
        </div>
      </div>
    </motion.div>;
}
function Step2() {
  const voices = ['Professional', 'Friendly', 'Playful', 'Inspiring', 'Educational'];
  const [selected, setSelected] = useState('Friendly');
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="space-y-6" data-id="element-198">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-199">
        Define your brand voice
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-200">
        {voices.map(voice => <motion.button key={voice} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setSelected(voice)} className={`p-6 rounded-xl border-2 transition-all ${selected === voice ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`} data-id="element-201">
            <p className="font-semibold text-gray-900 dark:text-white" data-id="element-202">
              {voice}
            </p>
          </motion.button>)}
      </div>
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl" data-id="element-203">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-204">
          Preview:
        </p>
        <p className="text-gray-900 dark:text-white italic" data-id="element-205">
          "Join us this weekend for our seasonal pumpkin spice latte! ☕🍂"
        </p>
      </div>
    </motion.div>;
}
function Step3() {
  const colors = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444'];
  const [selected, setSelected] = useState('#8B5CF6');
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="space-y-6" data-id="element-206">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-207">
        Choose your brand colors
      </h2>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4" data-id="element-208">
        {colors.map(color => <motion.button key={color} whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} onClick={() => setSelected(color)} className={`w-full aspect-square rounded-xl transition-all ${selected === color ? 'ring-4 ring-offset-2 ring-purple-600' : ''}`} style={{
        backgroundColor: color
      }} data-id="element-209" />)}
      </div>
      <div className="mt-6 p-6 rounded-xl" style={{
      backgroundColor: selected + '20'
    }} data-id="element-210">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-211">
          Your brand color will be used throughout your posts and campaigns
        </p>
      </div>
    </motion.div>;
}
function Step4() {
  const platforms = [{
    name: 'Instagram',
    connected: false
  }, {
    name: 'Facebook',
    connected: false
  }, {
    name: 'LinkedIn',
    connected: false
  }, {
    name: 'Google Business',
    connected: false
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="space-y-6" data-id="element-212">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-213">
        Connect your social accounts
      </h2>
      <p className="text-gray-600 dark:text-gray-400" data-id="element-214">
        You can connect these later in Settings
      </p>
      <div className="space-y-3" data-id="element-215">
        {platforms.map(platform => <div key={platform.name} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-700 transition-colors" data-id="element-216">
            <span className="font-medium text-gray-900 dark:text-white" data-id="element-217">
              {platform.name}
            </span>
            <button className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors" data-id="element-218">
              Connect
            </button>
          </div>)}
      </div>
    </motion.div>;
}